import React, { forwardRef, useMemo } from 'react';
import { Copy, Stack } from '@grubhubprod/gh-cookbook-react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { DataTable, DataTableRef } from 'src/components/DataTable';
import { EmployeeRoleFilter } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/EmployeeListTable/EmployeeRoleFilter';
import { buildCMUIPath, CMUIPage } from 'src/constants/urls';
import { createEmployeeListAdapter } from 'src/components/enterprise/features/Employee/EmployeeListPage/hooks/createEmployeeListAdapter';
import { createEmployeeListSelectionAdapter } from 'src/components/enterprise/features/Employee/EmployeeListPage/hooks/createEmployeeListSelectionAdapter';
import { EmployeeListTableWelcomeEmailButton } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/EmployeeListTable/EmployeeListTableWelcomeEmailButton';
import { EmployeeListTableDetailsLink } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/EmployeeListTable/EmployeeListTableDetailsLink';
import { useSentWelcomeEmails } from 'src/components/enterprise/features/Employee/EmployeeListPage/hooks/useSentWelcomeEmails';
import {
  EmployeeListFilters,
  getEmployeeNameForList,
  initialEmployeeListTableState,
} from 'src/components/enterprise/features/Employee/utils/employee.utils';
import { EmployeeSearchResultItem } from 'src/types/Employee';

enum EmployeeListTablePhoneColumnSpan {
  Selection = 1,
  Name = 4,
  Email = 5,
  Actions = 2,
}

export const EmployeeListTable = forwardRef<DataTableRef<EmployeeListFilters>>(
  EmployeeListTableContent
);

function EmployeeListTableContent(_props, ref): JSX.Element {
  const { corpId } = useAuth();
  const adapter = useMemo(() => createEmployeeListAdapter(corpId), [corpId]);
  const { isSent, markAsSent } = useSentWelcomeEmails();
  const selectionAdapter = useMemo(
    () => createEmployeeListSelectionAdapter({ corpId, onSent: markAsSent }),
    [corpId, markAsSent]
  );

  return (
    <DataTable.Provider<EmployeeSearchResultItem, EmployeeListFilters>
      ref={ref}
      adapter={adapter}
      initialState={initialEmployeeListTableState}
    >
      <DataTable.SelectionProvider
        adapter={selectionAdapter}
        idKey="dinerId"
        entityName="employee"
        primaryActionName="Email employees"
        secondaryActionName="Cancel"
      >
        <DataTable.Container>
          <DataTable.TitleBar>
            <DataTable.Title x="sm">Active Employees</DataTable.Title>
            <DataTable.Summary />
          </DataTable.TitleBar>
          <DataTable.Toolbar>
            <Stack x="lg">
              <DataTable.Search placeholder="Employee Name" />
            </Stack>
            <Stack minWidth={230}>
              <EmployeeRoleFilter />
            </Stack>
          </DataTable.Toolbar>
          <DataTable.Content>
            <DataTable.Header>
              <DataTable.SelectionColumnHeader
                phone={EmployeeListTablePhoneColumnSpan.Selection}
              />
              <DataTable.Column phone={EmployeeListTablePhoneColumnSpan.Name}>
                Employee Name
              </DataTable.Column>
              <DataTable.Column phone={EmployeeListTablePhoneColumnSpan.Email}>
                Email
              </DataTable.Column>
            </DataTable.Header>
            <DataTable.Body
              emptyComponent={
                <Copy variant="body">
                  There are currently no employees. Why don't you add some?
                </Copy>
              }
            >
              {(employee: EmployeeSearchResultItem): JSX.Element => (
                <DataTable.Row key={employee.id} id={employee.id}>
                  <DataTable.SelectableCell
                    id={employee.dinerId}
                    phone={EmployeeListTablePhoneColumnSpan.Selection}
                  />
                  <DataTable.Cell phone={EmployeeListTablePhoneColumnSpan.Name}>
                    <Copy variant="body">
                      {getEmployeeNameForList(employee)}
                    </Copy>
                  </DataTable.Cell>
                  <DataTable.Cell
                    phone={EmployeeListTablePhoneColumnSpan.Email}
                  >
                    <Copy variant="body">{employee.email}</Copy>
                  </DataTable.Cell>
                  <DataTable.Cell
                    phone={EmployeeListTablePhoneColumnSpan.Actions}
                    justifyContent="flex-end"
                  >
                    <EmployeeListTableDetailsLink
                      href={buildCMUIPath(CMUIPage.EMPLOYEE_DETAILS, {
                        corpId,
                        employeeId: employee.id,
                      })}
                    />
                    <EmployeeListTableWelcomeEmailButton
                      corpId={corpId}
                      dinerId={employee.dinerId}
                      isSent={isSent(employee.dinerId)}
                      onSent={markAsSent}
                    />
                  </DataTable.Cell>
                </DataTable.Row>
              )}
            </DataTable.Body>
            <DataTable.Footer>
              <DataTable.Pagination />
            </DataTable.Footer>
          </DataTable.Content>
        </DataTable.Container>
      </DataTable.SelectionProvider>
    </DataTable.Provider>
  );
}
